import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Steps, Button, Select, Upload, Form, Table, Row, Col, Input,
  Space, Typography, message, Tabs, Spin, Alert, Tag, InputNumber,
  Checkbox, Result, Divider, Radio, DatePicker,
} from 'antd';
import {
  FileExcelOutlined, GoogleOutlined, DatabaseOutlined,
  UploadOutlined, ArrowLeftOutlined, ImportOutlined,
  ExportOutlined, DownloadOutlined, HistoryOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { hasRole as checkRole } from '../utils/permissionUtils';
import commerceImportApi from '../services/commerceImportApi';
import CommerceImportFieldMapping from '../components/CommerceImportFieldMapping';
import {
  downloadAllCommerceImportTemplates,
  downloadCommerceImportTemplate,
  pickDefaultImportSheet,
} from '../utils/commerceImportTemplates';

const { Title, Text } = Typography;
const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const TARGET_KEYS = {
  commerce: ['customers', 'priceCards', 'products', 'collections', 'collectionLinks'],
  wpa: ['wpaGroupMembers', 'wpaGroupCustomers', 'wpaContactOverrides', 'wpaIdentities'],
};

const AUTO_MAP_ALIASES = {
  customers: {
    customerNo: ['customer_no', 'customerno', 'custno', 'cust_no', '客戶編號', 'customer'],
    branchNo: ['branch_no', 'branchno', '分店編號', 'branch'],
    name1: ['name1', 'name', 'name_en', 'nameen', 'name_en1', '英文名稱', 'english_name', 'customer_name'],
    nameLocaleLine1ZhTc: ['name_tc', 'name_zh_tc', 'name_traditional', '繁體', 'name_cn', 'namecn', 'name_cn1', '中文名稱', 'chinese_name'],
    contactName: ['contact_name', 'contactname', '聯絡人', 'contact'],
    tel: ['tel', 'phone', 'telephone', '電話'],
    email: ['email', '電郵', 'e-mail'],
    whatsappNo: ['whatsapp', 'whatsappno', 'whatsapp_no', 'wa'],
    salesmanName: ['salesman_name', 'salesmanname', '銷售員名稱', '业务员名称'],
    salesmanCode: ['salesman', 'salesman_code', 'salesmancode', '業務員代碼', '业务员代码', '銷售員編號', '销售员代码'],
    salesmanWhatsapp: ['salesman_whatsapp', 'salesmanwhatsapp', '銷售員whatsapp', '业务员whatsapp'],
    creditLimit: ['credit_limit', 'creditlimit', '信用額度'],
    priceType: ['price_type', 'pricetype', '價格類型', '价格类型'],
    billingAddressEn1: ['billing_address_en_1', 'billingaddressen1', '帳單地址英1', '帳單英文地址1', 'bill_addr_en_1'],
    billingAddressEn2: ['billing_address_en_2', 'billingaddressen2', '帳單地址英2', '帳單英文地址2', 'bill_addr_en_2'],
    billingAddressEn3: ['billing_address_en_3', 'billingaddressen3', '帳單地址英3', '帳單英文地址3', 'bill_addr_en_3'],
    billingAddressCn1: ['billing_address_cn_1', 'billingaddresscn1', '帳單地址中1', '帳單中文地址1', 'bill_addr_cn_1'],
    billingAddressCn2: ['billing_address_cn_2', 'billingaddresscn2', '帳單地址中2', '帳單中文地址2', 'bill_addr_cn_2'],
    billingAddressCn3: ['billing_address_cn_3', 'billingaddresscn3', '帳單地址中3', '帳單中文地址3', 'bill_addr_cn_3'],
    deliveryAddressEn1: ['delivery_address_en_1', 'deliveryaddressen1', '送貨地址英1', '送货地址英1', 'ship_addr_en_1'],
    deliveryAddressEn2: ['delivery_address_en_2', 'deliveryaddressen2', '送貨地址英2', 'ship_addr_en_2'],
    deliveryAddressEn3: ['delivery_address_en_3', 'deliveryaddressen3', '送貨地址英3', 'ship_addr_en_3'],
    deliveryAddressCn1: ['delivery_address_cn_1', 'deliveryaddresscn1', '送貨地址中1', '送货地址中1', 'ship_addr_cn_1'],
    deliveryAddressCn2: ['delivery_address_cn_2', 'deliveryaddresscn2', '送貨地址中2', 'ship_addr_cn_2'],
    deliveryAddressCn3: ['delivery_address_cn_3', 'deliveryaddresscn3', '送貨地址中3', 'ship_addr_cn_3'],
    deliveryDayMon: ['delivery_day_mon', 'deliverydaymon', '車期一', '送貨一', 'mon', '週一'],
    deliveryDayTue: ['delivery_day_tue', 'deliverydaytue', '車期二', 'tue', '週二'],
    deliveryDayWed: ['delivery_day_wed', 'deliverydaywed', '車期三', 'wed', '週三'],
    deliveryDayThu: ['delivery_day_thu', 'deliverydaythu', '車期四', 'thu', '週四'],
    deliveryDayFri: ['delivery_day_fri', 'deliverydayfri', '車期五', 'fri', '週五'],
    deliveryDaySat: ['delivery_day_sat', 'deliverydaysat', '車期六', 'sat', '週六'],
    deliveryDaySun: ['delivery_day_sun', 'deliverydaysun', '車期日', 'sun', '週日'],
    deliveryDayHoliday: ['delivery_day_holiday', 'deliverydayholiday', '公眾假期送貨', '假日送貨', 'holiday'],
  },
  priceCards: {
    customerNo: ['customer_no', 'customerno', 'custno', '客戶編號'],
    branchNo: ['branch_no', 'branchno', '分店編號'],
    itemNo: ['item_no', 'itemno', 'product_no', '產品編號', 'sku'],
    unitPrice: ['unit_price', 'unitprice', 'price', '單價'],
    showPrice: ['show_price', 'showprice', '顯示價格'],
    validFrom: ['valid_from', 'validfrom', '生效日期', 'start_date'],
    validTo: ['valid_to', 'validto', '結束日期', 'end_date'],
    priceType: ['price_type', 'pricetype', '價格類型', '价格类型'],
    source: ['source', '來源'],
  },
  products: {
    name: ['name', 'product_name', 'productname', '名稱', '品名', 'title'],
    productRetailerId: ['retailer_id', 'product_retailer_id', 'sku', 'content_id', 'contentid'],
    description: ['description', 'desc', '描述'],
    linkUrl: ['link', 'url', 'link_url', '連結'],
    imageUrl: ['image', 'image_url', 'imageurl', '圖片'],
    brand: ['brand', '品牌'],
    price: ['price', '價格'],
    price1: ['price_1', 'price1', '區間一', '单价1'],
    validFrom1: ['valid_from_1', 'validfrom1', '生效起1', '开始1'],
    validTo1: ['valid_to_1', 'validto1', '生效訖1', '结束1'],
    price2: ['price_2', 'price2', '區間二', '单价2'],
    validFrom2: ['valid_from_2', 'validfrom2', '生效起2', '开始2'],
    validTo2: ['valid_to_2', 'validto2', '生效訖2', '结束2'],
    currency: ['currency', '幣別'],
    status: ['status', '狀態'],
    unitOfMeasureLocaleEn: ['unit_of_measure', 'unitofmeasure', 'uom', 'quantity_unit', 'unit', 'unit_of_measure_en', 'unitofmeasure_en', 'uom_en', 'unit_en', 'quantity_unit_en', 'eunitmeasur', '數量單位', '数量单位'],
    unitOfMeasureLocaleZhTc: ['unit_of_measure_zh_tc', 'unit_of_measure_zhtc', 'unitofmeasure_zhtc', 'uom_zh_tc', 'uom_zhtc', 'unit_zhtc', 'cunitmeasur', '數量單位繁', '數量單位_繁', '數量單位zhtc', '數量單位_zh-TC'],
    unitOfMeasureLocaleZhSc: ['unit_of_measure_zh_sc', 'unit_of_measure_zhsc', 'unitofmeasure_zhsc', 'uom_zh_sc', 'uom_zhsc', 'unit_zhsc', 'cunitmeasur', '数量单位简', '数量单位_简', '数量单位zhsc', '数量单位_zh-SC'],
    unitOfBrokenPackLocaleEn: ['unit_of_brokenpack', 'unitofbrokenpack', 'broken_pack', 'brokenpack', 'ubp', 'broken_pack_unit', 'brokenpack_unit', 'unit_of_brokenpack_en', 'unitofbrokenpack_en', 'brokenpack_en', 'ubp_en', 'epackage', '散裝單位', '散装单位'],
    unitOfBrokenPackLocaleZhTc: ['unit_of_brokenpack_zh_tc', 'unit_of_brokenpack_zhtc', 'unitofbrokenpack_zhtc', 'brokenpack_zh_tc', 'brokenpack_zhtc', 'ubp_zh_tc', 'ubp_zhtc', 'cpackage', '散裝單位繁', '散裝單位_繁', '散裝單位zhtc', '散裝單位_zh-TC'],
    unitOfBrokenPackLocaleZhSc: ['unit_of_brokenpack_zh_sc', 'unit_of_brokenpack_zhsc', 'unitofbrokenpack_zhsc', 'brokenpack_zh_sc', 'brokenpack_zhsc', 'ubp_zh_sc', 'ubp_zhsc', 'cpackage', '散装单位简', '散装单位_简', '散装单位zhsc', '散装单位_zh-SC'],
    qtyPerUnit: ['qty_per_unit', 'qtyperunit', 'qty_per', 'quantity_per_unit', 'qtyperuom', 'qty_per_uom', 'pack_size', 'packsize', 'units_per_pack', 'unitsperpack', '每單位數量', '每单位数量', '每單位', '每单位', '包裝量', '包装量'],
    minOrderQuantity: ['moq', 'min_order_quantity', 'minorderquantity', 'minorderqty', '最小訂量', '起订量', '最小訂單數量', '起訂量'],
    oqm: ['oqm', 'original_quality_manufacturer', 'originalqualitymanufacturer', 'OQM'],
    color: ['color', '顏色'],
    size: ['size', '尺寸'],
  },
  collections: {
    name: ['name', 'collection_name', '名稱', '集合名稱'],
    externalCollectionKey: ['external_key', 'externalkey', 'collection_key', 'key'],
    description: ['description', 'desc', '描述'],
    metaCatalogId: ['meta_catalog_id', 'catalogid', 'catalog'],
  },
  collectionLinks: {
    collectionKey: ['collection_key', 'collectionkey', 'collection', '集合'],
    productRetailerId: ['product_retailer_id', 'retailer_id', 'sku', 'product', '商品'],
  },
  wpaGroupMembers: {
    groupName: ['group_name', 'groupname', 'group', '群組', '群組名稱'],
    loginId: ['login_id', 'loginid', '登入帳號', 'login', 'email', '帳號'],
  },
  wpaGroupCustomers: {
    groupName: ['group_name', 'groupname', 'group', '群組'],
    customerNo: ['customer_no', 'customerno', '客戶編號'],
    branchNo: ['branch_no', 'branchno', '分店編號'],
  },
  wpaContactOverrides: {
    loginId: ['login_id', 'loginid', '登入帳號', 'login', 'email', '帳號'],
    roleKey: ['role_key', 'rolekey', 'role', '角色'],
    defaultCustomerNo: ['default_customer', 'default_customer_no', '預設客戶'],
    defaultBranchNo: ['default_branch', 'default_branch_no', '預設分店'],
    customerNos: ['customer_nos', 'customers', '可下單客戶'],
    status: ['status', '狀態'],
  },
  wpaIdentities: {
    loginId: ['login_id', 'loginid', '登入帳號', 'login', 'email', '帳號'],
    email: ['email', '電郵'],
    phoneNo: ['phone_no', 'phoneno', 'phone', 'whatsapp', 'whatsapp_number', '電話'],
    displayName: ['display_name', 'displayname', '名稱', '顯示名稱'],
    password: ['password', 'pwd', '密碼'],
    status: ['status', '狀態'],
  },
};

const TARGET_I18N_MAP = {
  customers: 'targetCustomers',
  priceCards: 'targetPriceCards',
  products: 'targetProducts',
  collections: 'targetCollections',
  collectionLinks: 'targetCollectionLinks',
  wpaGroupMembers: 'targetWpaGroupMembers',
  wpaGroupCustomers: 'targetWpaGroupCustomers',
  wpaContactOverrides: 'targetWpaContactOverrides',
  wpaIdentities: 'targetWpaIdentities',
};

/**
 * 與後端 CommerceImportScheduleRunner.TryGetRawField / HasRawColumn 一致：
 * 依 mapping 設定的來源欄名從一列取值（欄名不分大小寫，對應 SQL / ERP 常見命名）。
 */
function getRowValueCaseInsensitive(row, sourceCol) {
  if (row == null || sourceCol == null) return { found: false, value: undefined };
  const t = String(sourceCol).trim();
  if (!t) return { found: false, value: undefined };
  if (Object.prototype.hasOwnProperty.call(row, t)) return { found: true, value: row[t] };
  const tl = t.toLowerCase();
  for (const k of Object.keys(row)) {
    if (String(k).toLowerCase() === tl) return { found: true, value: row[k] };
  }
  return { found: false, value: undefined };
}

function generateAutoMapping(targetEntity, columns) {
  const aliases = AUTO_MAP_ALIASES[targetEntity] || {};
  const mapping = {};
  columns.forEach(col => {
    const norm = col.dataIndex.toLowerCase().replace(/[\s_-]+/g, '');
    for (const [target, aliasList] of Object.entries(aliases)) {
      if (mapping[target]) continue;
      const targetNorm = target.toLowerCase().replace(/[\s_-]+/g, '');
      if (norm === targetNorm) {
        mapping[target] = col.dataIndex;
        continue;
      }
      for (const alias of aliasList) {
        if (norm === alias.replace(/[\s_-]+/g, '') || norm.includes(alias.replace(/[\s_-]+/g, ''))) {
          mapping[target] = col.dataIndex;
          break;
        }
      }
    }
  });
  return mapping;
}

const CommerceImportPage = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const editScheduleId = searchParams.get('editSchedule');

  const tgt = (key) => t(`commerceImport.${TARGET_I18N_MAP[key]}`) || key;

  const [activeTab, setActiveTab] = useState('import');
  const [domain, setDomain] = useState('commerce');
  const [targetEntity, setTargetEntity] = useState('customers');
  const [currentStep, setCurrentStep] = useState(0);
  const [importType, setImportType] = useState('excel');
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [mappingForm] = Form.useForm();
  const [importStatus, setImportStatus] = useState('idle');
  const [importResults, setImportResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const [saveAsSchedule, setSaveAsSchedule] = useState(false);
  const [scheduleName, setScheduleName] = useState('');
  const [scheduleType, setScheduleType] = useState('interval');
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [editingScheduleData, setEditingScheduleData] = useState(null);

  const [excelConfig, setExcelConfig] = useState({ filePath: '', sheetName: '', availableSheets: [] });
  const [googleConfig, setGoogleConfig] = useState({ url: '', sheetName: '', spreadsheetId: '', availableSheets: [], fileType: '' });
  const [sqlConfig, setSqlConfig] = useState({ server: '', database: '', username: '', password: '', table: '', query: '' });
  const [sqlPreviewMeta, setSqlPreviewMeta] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const [exportMode, setExportMode] = useState('split');
  const [exportStatus, setExportStatus] = useState(null);
  const [exportDateFrom, setExportDateFrom] = useState(null);
  const [exportDateTo, setExportDateTo] = useState(null);
  const [exportOrderStatus, setExportOrderStatus] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [allowPasswordUpdate, setAllowPasswordUpdate] = useState(false);

  const showWpa = checkRole(userInfo, 'commerce.wpaPermissions');
  const isTenantAdmin = checkRole(userInfo, 'Tenant_Admin');

  const allTargetKeys = useMemo(() => TARGET_KEYS[domain] || TARGET_KEYS.commerce, [domain]);

  useEffect(() => {
    if (isTenantAdmin) {
    const token = localStorage.getItem('token');
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(data => setCompanies(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
  }, [isTenantAdmin]);

  useEffect(() => {
    if (!isTenantAdmin && importType === 'sql') {
      setImportType('excel');
    }
  }, [isTenantAdmin, importType]);

  useEffect(() => {
    if (!excelConfig.availableSheets.length) return;
    const nextSheet = pickDefaultImportSheet(excelConfig.availableSheets, targetEntity, currentLanguage);
    if (nextSheet) {
      setExcelConfig((prev) => ({ ...prev, sheetName: nextSheet }));
    }
  }, [targetEntity, currentLanguage, excelConfig.availableSheets]);

  useEffect(() => {
    if (editScheduleId) {
      (async () => {
        try {
          const res = await commerceImportApi.getSchedules({ page: 1, pageSize: 500 });
          if (res.success) {
            const sched = res.schedules.find(s => s.id === editScheduleId);
            if (sched) {
              setEditingScheduleData(sched);
              setScheduleName(sched.name);
              setSaveAsSchedule(true);
              setScheduleType(sched.scheduleType || 'interval');
              setIntervalMinutes(sched.intervalMinutes || 60);
              setTargetEntity(sched.targetEntity || 'customers');
              setDomain(sched.targetEntity?.startsWith('wpa') ? 'wpa' : 'commerce');
              const srcCfg = typeof sched.sourceConfig === 'string' ? JSON.parse(sched.sourceConfig) : sched.sourceConfig;
              const fldMap = typeof sched.fieldMapping === 'string' ? JSON.parse(sched.fieldMapping) : sched.fieldMapping;
              if (sched.importType === 'sql' && !isTenantAdmin) {
                setImportType('excel');
                message.warning(t('commerceImport.sqlTenantAdminOnly'));
              } else {
                setImportType(sched.importType || 'excel');
              }
              if (sched.importType === 'excel') setExcelConfig(prev => ({ ...prev, ...srcCfg }));
              else if (sched.importType === 'google') setGoogleConfig(prev => ({ ...prev, ...srcCfg }));
              else if (sched.importType === 'sql' && isTenantAdmin) setSqlConfig(prev => ({ ...prev, ...srcCfg }));
              if (fldMap) { setFieldMapping(fldMap); mappingForm.setFieldsValue(fldMap); }
            }
          }
        } catch (e) { console.error(e); }
      })();
    }
  }, [editScheduleId, mappingForm, isTenantAdmin, t]);

  const handleUpload = useCallback(async (info) => {
    const { file } = info;
    setUploading(true);
    try {
      const result = await commerceImportApi.uploadExcelFile(file);
      if (result.success) {
        const sheets = result.sheets || [];
        const defaultSheet = pickDefaultImportSheet(sheets, targetEntity, currentLanguage);
        setExcelConfig(prev => ({
          ...prev,
          filePath: result.filePath,
          availableSheets: sheets,
          sheetName: defaultSheet,
        }));
        message.success(t('contactImport.fileUploaded'));
        if (sheets.length > 1) {
          message.info(t('commerceImport.multiSheetPickHint').replace('{count}', String(sheets.length)));
        }
      }
    } catch (e) {
      message.error(t('commerceImport.parseFailed') + ': ' + (e.response?.data?.message || e.message));
    } finally { setUploading(false); }
  }, [t, targetEntity, currentLanguage]);

  const loadPreview = useCallback(async (source) => {
    if (source === 'sql' && !isTenantAdmin) {
      message.error(t('commerceImport.sqlTenantAdminOnly'));
      return;
    }
    setLoading(true);
    try {
      let result;
      if (source === 'excel') result = await commerceImportApi.loadFromExcel({ filePath: excelConfig.filePath, sheetName: excelConfig.sheetName });
      else if (source === 'google') result = await commerceImportApi.loadFromGoogleDocs({ url: googleConfig.url, sheetName: googleConfig.sheetName, spreadsheetId: googleConfig.spreadsheetId, fileType: googleConfig.fileType });
      else if (source === 'sql') result = await commerceImportApi.loadFromSql({ ...sqlConfig, previewMode: true });
      if (result?.success) {
        const cols = (result.columns || []).map(c => ({ title: c, dataIndex: c, key: c }));
        setPreviewData(result.data || []);
        setPreviewColumns(cols);
        if (source === 'sql') {
          setSqlPreviewMeta({
            totalMatchingRows: result.totalMatchingRows,
            previewTopApplied: result.previewTopApplied,
            previewTopSkippedForWithCte: result.previewTopSkippedForWithCte,
          });
        } else {
          setSqlPreviewMeta(null);
        }
        const autoMap = generateAutoMapping(targetEntity, cols);
        setFieldMapping(autoMap);
        mappingForm.setFieldsValue(autoMap);
        setCurrentStep(1);
        message.success(t('commerceImport.parseSuccess').replace('{count}', (result.data || []).length));
      } else {
        message.error(result?.message || t('commerceImport.parseFailed'));
      }
    } catch (e) {
      message.error(t('commerceImport.parseFailed') + ': ' + (e.response?.data?.message || e.message));
    } finally { setLoading(false); }
  }, [excelConfig, googleConfig, sqlConfig, targetEntity, mappingForm, isTenantAdmin, t]);

  const handleGoogleVerify = useCallback(async () => {
    if (!googleConfig.url) { message.warning(t('contactImport.pleaseEnterGoogleSheetsUrl')); return; }
    setLoading(true);
    try {
      const result = await commerceImportApi.uploadGoogleSheetsUrl(googleConfig.url);
      if (result.success) {
        setGoogleConfig(prev => ({ ...prev, spreadsheetId: result.spreadsheetId, availableSheets: result.sheets || [], fileType: result.fileType, sheetName: result.sheets?.[0] || '' }));
        message.success(t('contactImport.googleSheetsVerified'));
      }
    } catch (e) {
      message.error(t('contactImport.googleSheetsVerificationFailed') + (e.response?.data?.message || e.message));
    } finally { setLoading(false); }
  }, [googleConfig.url, t]);

  const handleTestSql = useCallback(async () => {
    if (!isTenantAdmin) {
      message.error(t('commerceImport.sqlTenantAdminOnly'));
      return;
    }
    try {
      const result = await commerceImportApi.testSqlConnection(sqlConfig);
      if (result.success) message.success(t('contactImport.testConnection') + ' OK');
      else message.error(result.message);
    } catch (e) {
      message.error(t('contactImport.testConnection') + ': ' + (e.response?.data?.message || e.message));
    }
  }, [sqlConfig, isTenantAdmin, t]);

  const handleImport = useCallback(async () => {
    if (saveAsSchedule && !scheduleName) { message.error(t('contactImport.scheduleNameRequired')); return; }
    setImportStatus('importing');
    try {
      try {
        await mappingForm.validateFields();
      } catch (validateErr) {
        if (validateErr?.errorFields?.length) {
          setImportStatus('idle');
          message.warning(t('commerceImport.fieldMappingFormIncomplete'));
          return;
        }
        throw validateErr;
      }
      const rawMapping = mappingForm.getFieldsValue();
      const mapping = Object.fromEntries(
        Object.entries(rawMapping).filter(([, v]) => v !== undefined && v !== null && v !== '')
      );
      let rowsSource = previewData;
      if (importType === 'sql') {
        if (!isTenantAdmin) {
          setImportStatus('idle');
          message.error(t('commerceImport.sqlTenantAdminOnly'));
          return;
        }
        const hideLoading = message.loading(t('commerceImport.sqlLoadingFullForImport'), 0);
        try {
          const full = await commerceImportApi.loadFromSql({ ...sqlConfig, previewMode: false });
          hideLoading();
          if (!full.success) {
            setImportStatus('idle');
            message.error(full.message || t('commerceImport.parseFailed'));
            return;
          }
          rowsSource = full.data || [];
        } catch (e) {
          hideLoading();
          setImportStatus('idle');
          message.error(t('commerceImport.parseFailed') + ': ' + (e.response?.data?.message || e.message));
          return;
        }
      }
      const rows = rowsSource.map((row, idx) => {
        const mapped = {};
        for (const [targetKey, sourceCol] of Object.entries(mapping)) {
          if (!sourceCol) continue;
          const { found, value } = getRowValueCaseInsensitive(row, sourceCol);
          if (!found) continue;
          mapped[targetKey] = typeof value === 'string' ? value.trim() : value;
        }
        mapped._rowNumber = idx + 1;
        return mapped;
      });

      let result;
      if (targetEntity.startsWith('wpa')) result = await commerceImportApi.wpaImport(targetEntity, rows, allowPasswordUpdate);
      else result = await commerceImportApi.executeImport(targetEntity, rows, selectedCompanyId);
      setImportResults(result);
      setImportStatus('success');
      setCurrentStep(2);

      if (saveAsSchedule && scheduleName) {
        try {
          const sourceConfig = importType === 'excel' ? excelConfig : importType === 'google' ? googleConfig : sqlConfig;
          await commerceImportApi.createSchedule({ name: scheduleName, importType, targetEntity, isScheduled: true, scheduleType, intervalMinutes: scheduleType === 'interval' ? intervalMinutes : null, sourceConfig, fieldMapping: mapping, allowUpdateDuplicates: false });
          setScheduleSaved(true);
        } catch (e) { setScheduleError(e.response?.data?.message || e.message || t('commerceImport.scheduleSaveFailed')); }
      }
    } catch (e) {
      setImportStatus('error');
      message.error(t('commerceImport.importFailed') + ': ' + (e.response?.data?.message || e.message));
    }
  }, [previewData, mappingForm, targetEntity, saveAsSchedule, scheduleName, scheduleType, intervalMinutes, importType, excelConfig, googleConfig, sqlConfig, selectedCompanyId, allowPasswordUpdate, isTenantAdmin, t]);

  const handleExport = useCallback(async () => {
    setExportLoading(true);
    try {
      const params = { mode: exportMode, companyId: selectedCompanyId || undefined, status: exportOrderStatus || undefined, dateFrom: exportDateFrom?.toISOString() || undefined, dateTo: exportDateTo?.toISOString() || undefined };
      const result = await commerceImportApi.exportSalesOrders(params);
      if (result.success) { setExportData(result); setExportStatus('success'); message.success(t('commerceImport.exportDone')); }
    } catch (e) {
      message.error(t('commerceImport.exportFailed') + ': ' + (e.response?.data?.message || e.message));
    } finally { setExportLoading(false); }
  }, [exportMode, selectedCompanyId, exportOrderStatus, exportDateFrom, exportDateTo, t]);

  const downloadCsv = useCallback((data, filename) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const csv = [headers.join(','), ...data.map(row => headers.map(h => { const v = row[h] ?? ''; return typeof v === 'string' && v.includes(',') ? `"${v}"` : v; }).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }, []);

  const resetImport = useCallback(() => {
    setCurrentStep(0); setPreviewData([]); setPreviewColumns([]); setFieldMapping({});
    setSqlPreviewMeta(null);
    mappingForm.resetFields(); setImportStatus('idle'); setImportResults(null);
    setScheduleSaved(false); setScheduleError(''); setFileList([]);
  }, [mappingForm]);

  const sourceTypeColor = importType === 'excel' ? '#52c41a' : importType === 'google' ? '#4285f4' : '#722ed1';

  const importSourceCards = useMemo(() => {
    const cards = [
      { key: 'excel', icon: <FileExcelOutlined style={{ fontSize: 32 }} />, color: '#52c41a', label: t('contactImport.excelFile') },
      { key: 'google', icon: <GoogleOutlined style={{ fontSize: 32 }} />, color: '#4285f4', label: t('contactImport.googleSheets') },
    ];
    if (isTenantAdmin) {
      cards.push({ key: 'sql', icon: <DatabaseOutlined style={{ fontSize: 32 }} />, color: '#722ed1', label: t('contactImport.sqlDatabase') });
    }
    return cards;
  }, [isTenantAdmin, t]);

  const renderSourceCards = () => (
    <Row gutter={16} style={{ marginBottom: 24 }}>
      {importSourceCards.map(s => (
        <Col span={isTenantAdmin ? 8 : 12} key={s.key}>
          <Card hoverable onClick={() => setImportType(s.key)} style={{ textAlign: 'center', borderColor: importType === s.key ? s.color : undefined, borderWidth: importType === s.key ? 2 : 1 }}>
            <div style={{ color: s.color }}>{s.icon}</div>
            <div style={{ marginTop: 8, fontWeight: importType === s.key ? 600 : 400 }}>{s.label}</div>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const handleDownloadTemplate = useCallback(() => {
    downloadCommerceImportTemplate(targetEntity, currentLanguage);
    message.success(t('commerceImport.templateDownloaded'));
  }, [targetEntity, currentLanguage, t]);

  const handleDownloadAllTemplates = useCallback((locale) => {
    downloadAllCommerceImportTemplates(locale);
    message.success(t('commerceImport.templateAllDownloaded'));
  }, [t]);

  const renderTemplateDownloadButtons = () => (
    <Space wrap>
      <Button icon={<FileExcelOutlined />} onClick={handleDownloadTemplate}>
        {t('commerceImport.downloadImportTemplate')}
      </Button>
      <Button icon={<FileExcelOutlined />} onClick={() => handleDownloadAllTemplates('zh-TC')}>
        {t('commerceImport.downloadAllImportTemplatesZh')}
      </Button>
      <Button icon={<FileExcelOutlined />} onClick={() => handleDownloadAllTemplates('en')}>
        {t('commerceImport.downloadAllImportTemplatesEn')}
      </Button>
    </Space>
  );

  const renderExcelConfig = () => (
    <Card size="small" title={t('contactImport.excelConfig')}>
      <Space wrap align="start">
        <Upload customRequest={handleUpload} accept=".xlsx,.xls,.csv" showUploadList={false} onChange={({ fileList }) => setFileList(fileList)}>
          <Button icon={<UploadOutlined />} loading={uploading}>{t('contactImport.uploadExcelFile')}</Button>
        </Upload>
        {renderTemplateDownloadButtons()}
      </Space>
      {excelConfig.filePath && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">{t('contactImport.fileUploaded')}</Text>
          {excelConfig.availableSheets.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text strong>
                {excelConfig.availableSheets.length > 1
                  ? t('commerceImport.selectImportSheet').replace('{count}', String(excelConfig.availableSheets.length))
                  : t('contactImport.selectSheet')}
              </Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={excelConfig.sheetName}
                onChange={v => setExcelConfig(prev => ({ ...prev, sheetName: v }))}
                placeholder={t('contactImport.selectSheet')}
                showSearch
                optionFilterProp="label"
                options={excelConfig.availableSheets.map(s => ({ value: s, label: s }))}
              />
            </div>
          )}
          <Button type="primary" style={{ marginTop: 12 }} onClick={() => loadPreview('excel')} disabled={!excelConfig.filePath || !excelConfig.sheetName} loading={loading}>
            {t('contactImport.nextStep')}
          </Button>
        </div>
      )}
    </Card>
  );

  const renderGoogleConfig = () => (
    <Card size="small" title={t('contactImport.googleDocsConfig')}>
      <div style={{ marginBottom: 12 }}>
        {renderTemplateDownloadButtons()}
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          {t('commerceImport.templateGoogleSheetsHint')}
        </Text>
      </div>
      <Input placeholder={t('contactImport.googleDocsUrl')} value={googleConfig.url} onChange={e => setGoogleConfig(prev => ({ ...prev, url: e.target.value }))} />
      <Button style={{ marginTop: 8 }} onClick={handleGoogleVerify} loading={loading}>{t('contactImport.urlVerified') || 'Verify URL'}</Button>
      {googleConfig.spreadsheetId && (
        <div style={{ marginTop: 12 }}>
          <Text type="success">{t('contactImport.urlVerified')}</Text>
          {googleConfig.fileType === 'googlesheets' && googleConfig.availableSheets.length > 0 && (
            <Select style={{ width: '100%', marginTop: 8 }} value={googleConfig.sheetName} onChange={v => setGoogleConfig(prev => ({ ...prev, sheetName: v }))} placeholder={t('contactImport.selectSheet')}>
              {googleConfig.availableSheets.map(s => <Option key={s} value={s}>{s}</Option>)}
            </Select>
          )}
          {googleConfig.fileType === 'excel' && (
            <Alert type="info" message={t('contactImport.detectedExcelFile')} style={{ marginTop: 8 }} showIcon />
          )}
          <Button type="primary" style={{ marginTop: 12 }} onClick={() => loadPreview('google')} loading={loading} disabled={!googleConfig.spreadsheetId}>
            {t('contactImport.nextStep')}
          </Button>
        </div>
      )}
    </Card>
  );

  const renderSqlConfig = () => (
    <Card size="small" title={t('contactImport.sqlConfig')}>
      <Row gutter={12}>
        <Col span={12}><Input placeholder={t('contactImport.server')} value={sqlConfig.server} onChange={e => setSqlConfig(prev => ({ ...prev, server: e.target.value }))} style={{ marginBottom: 8 }} /></Col>
        <Col span={12}><Input placeholder={t('contactImport.database')} value={sqlConfig.database} onChange={e => setSqlConfig(prev => ({ ...prev, database: e.target.value }))} style={{ marginBottom: 8 }} /></Col>
        <Col span={12}><Input placeholder={t('contactImport.username')} value={sqlConfig.username} onChange={e => setSqlConfig(prev => ({ ...prev, username: e.target.value }))} style={{ marginBottom: 8 }} /></Col>
        <Col span={12}><Input.Password placeholder={t('contactImport.password')} value={sqlConfig.password} onChange={e => setSqlConfig(prev => ({ ...prev, password: e.target.value }))} style={{ marginBottom: 8 }} /></Col>
        <Col span={12}><Input placeholder={t('contactImport.tableName')} value={sqlConfig.table} onChange={e => setSqlConfig(prev => ({ ...prev, table: e.target.value }))} style={{ marginBottom: 8 }} /></Col>
        <Col span={24}><TextArea rows={3} placeholder={t('contactImport.customQuery')} value={sqlConfig.query} onChange={e => setSqlConfig(prev => ({ ...prev, query: e.target.value }))} /></Col>
      </Row>
      <Space style={{ marginTop: 12 }}>
        <Button onClick={handleTestSql}>{t('contactImport.testConnection')}</Button>
        <Button type="primary" onClick={() => loadPreview('sql')} loading={loading} disabled={!sqlConfig.server || !sqlConfig.database || (!sqlConfig.table && !sqlConfig.query)}>
          {t('contactImport.nextStep')}
        </Button>
      </Space>
    </Card>
  );

  const renderImportWizard = () => (
    <div>
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
        <Space split={<Divider type="vertical" />}>
          <span>{t('commerceImport.importTarget')}：<Tag color="blue">{tgt(targetEntity)}</Tag></span>
          <span>{t('contactImport.dataSourceType')}：<Tag color={sourceTypeColor}>{importType}</Tag></span>
          {isTenantAdmin && selectedCompanyId && (
            <span>{t('commerceImport.company')}：<Tag>{companies.find(c => c.id === selectedCompanyId)?.name || selectedCompanyId}</Tag></span>
          )}
        </Space>
      </Card>

      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title={t('commerceImport.stepSource')} description={t('commerceImport.stepSourceDesc')} />
        <Step title={t('commerceImport.stepMapping')} description={t('commerceImport.stepMappingDesc')} />
        <Step title={t('commerceImport.stepResult')} description={t('commerceImport.stepResultDesc')} />
      </Steps>

      {currentStep === 0 && (
        <div>
          {renderSourceCards()}
          {importType === 'excel' && renderExcelConfig()}
          {importType === 'google' && renderGoogleConfig()}
          {importType === 'sql' && isTenantAdmin && renderSqlConfig()}
        </div>
      )}

      {currentStep === 1 && (
        <div>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Checkbox checked={saveAsSchedule} onChange={e => setSaveAsSchedule(e.target.checked)} disabled={!!editScheduleId}>
              {t('contactImport.saveAsSchedule')}
            </Checkbox>
            {saveAsSchedule && (
              <div style={{ marginTop: 12 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input placeholder={t('contactImport.scheduleNamePlaceholder')} value={scheduleName} onChange={e => setScheduleName(e.target.value)} />
                  <Radio.Group value={scheduleType} onChange={e => setScheduleType(e.target.value)}>
                    <Radio value="interval">{t('contactImport.everyXMinutes')}</Radio>
                    <Radio value="daily">{t('contactImport.daily')}</Radio>
                    <Radio value="weekly">{t('contactImport.weekly')}</Radio>
                  </Radio.Group>
                  {scheduleType === 'interval' && (
                    <InputNumber min={1} value={intervalMinutes} onChange={setIntervalMinutes} addonAfter={t('commerceImport.minutes')} />
                  )}
                </Space>
              </div>
            )}
          </Card>

          {targetEntity === 'wpaIdentities' && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Checkbox checked={allowPasswordUpdate} onChange={e => setAllowPasswordUpdate(e.target.checked)}>
                {t('commerceImport.wpaAllowPasswordUpdate')}
              </Checkbox>
            </Card>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Card title={t('contactImport.dataPreview')} size="small" bodyStyle={{ padding: 0 }}>
                {importType === 'sql' && sqlPreviewMeta && (
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                    {sqlPreviewMeta.totalMatchingRows != null && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        {t('commerceImport.sqlPreviewTotalRows').replace('{count}', String(sqlPreviewMeta.totalMatchingRows))}
                      </Text>
                    )}
                    {sqlPreviewMeta.previewTopApplied != null && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                        {t('commerceImport.sqlPreviewTopNote').replace('{n}', String(sqlPreviewMeta.previewTopApplied))}
                      </Text>
                    )}
                    {sqlPreviewMeta.previewTopSkippedForWithCte && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                        {t('commerceImport.sqlPreviewWithCteNote')}
                      </Text>
                    )}
                  </div>
                )}
                <Table dataSource={previewData.slice(0, 100)} columns={previewColumns.map(c => ({ ...c, width: 150, ellipsis: true }))} scroll={{ x: 'max-content', y: 400 }} size="small" pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100], size: 'small' }} rowKey={(_, i) => i} />
              </Card>
            </Col>
            <Col span={12}>
              <Card title={t('contactImport.fieldMapping')} size="small">
                <CommerceImportFieldMapping form={mappingForm} columns={previewColumns} targetEntity={targetEntity} />
              </Card>
            </Col>
          </Row>

          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => setCurrentStep(0)}>{t('contactImport.previous')}</Button>
              <Button type="primary" icon={<ImportOutlined />} onClick={handleImport} loading={importStatus === 'importing'}>
                {t('contactImport.startImport')}
              </Button>
            </Space>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div>
          {importStatus === 'success' && importResults && (
            <Result status="success" title={t('commerceImport.importDoneTitle')}
              subTitle={
                <Text>
                  {t('commerceImport.total')} {importResults.total}
                  {importResults.created != null && ` · ${t('commerceImport.created')} ${importResults.created}`}
                  {importResults.updated != null && ` · ${t('commerceImport.updated')} ${importResults.updated}`}
                  {importResults.added != null && ` · ${t('commerceImport.added')} ${importResults.added}`}
                  {importResults.linked != null && ` · ${t('commerceImport.linked')} ${importResults.linked}`}
                  {importResults.skipped != null && ` · ${t('commerceImport.skipped')} ${importResults.skipped}`}
                  {importResults.errors > 0 && ` · ${t('commerceImport.errorsCount')} ${importResults.errors}`}
                </Text>
              }
            />
          )}
          {importResults?.errorList?.length > 0 && (
            <Alert type="warning" message={t('commerceImport.errorDetails')}
              description={<ul style={{ maxHeight: 200, overflow: 'auto' }}>{importResults.errorList.map((e, i) => <li key={i}>{e}</li>)}</ul>}
              style={{ marginBottom: 16 }}
            />
          )}
          {scheduleSaved && !scheduleError && <Alert type="success" message={t('commerceImport.scheduleSaved')} style={{ marginBottom: 16 }} showIcon />}
          {scheduleError && <Alert type="error" message={`${t('commerceImport.scheduleSaveFailed')}：${scheduleError}`} style={{ marginBottom: 16 }} showIcon />}
          <Space>
            <Button onClick={resetImport}>{t('contactImport.importMore')}</Button>
            <Button type="link" onClick={() => navigate('/commerce/import-schedule')}>{t('commerceImport.scheduleGotoManage')}</Button>
        </Space>
        </div>
      )}
    </div>
  );

  const renderExportTab = () => (
    <div>
      <Card title={t('commerceImport.exportTitle')} style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Text strong>{t('commerceSalesOrders.status')}</Text>
            <Select style={{ width: '100%', marginTop: 4 }} allowClear placeholder={t('commerceImport.exportAllStatus')} value={exportOrderStatus} onChange={setExportOrderStatus}>
              <Option value="Pending">{t('commerceSalesOrders.statusPending')}</Option>
              <Option value="Confirmed">{t('commerceSalesOrders.statusConfirmed')}</Option>
              <Option value="Processing">{t('commerceSalesOrders.statusProcessing')}</Option>
              <Option value="Completed">{t('commerceSalesOrders.statusCompleted')}</Option>
              <Option value="Cancelled">{t('commerceSalesOrders.statusCancelled')}</Option>
            </Select>
          </Col>
          <Col span={8}>
            <Text strong>{t('commerceImport.exportDateRange')}</Text>
            <RangePicker style={{ width: '100%', marginTop: 4 }} onChange={(dates) => { setExportDateFrom(dates?.[0]?.toDate() || null); setExportDateTo(dates?.[1]?.toDate() || null); }} />
          </Col>
          <Col span={6}>
            <Text strong>{t('commerceImport.exportMode')}</Text>
            <Radio.Group style={{ display: 'block', marginTop: 4 }} value={exportMode} onChange={e => setExportMode(e.target.value)}>
              <Radio value="split">{t('commerceImport.exportModeSplit')}</Radio>
              <Radio value="flat">{t('commerceImport.exportModeFlat')}</Radio>
            </Radio.Group>
          </Col>
          {isTenantAdmin && (
            <Col span={4}>
              <Text strong>{t('commerceImport.company')}</Text>
              <Select style={{ width: '100%', marginTop: 4 }} allowClear placeholder={t('commerceImport.exportAllStatus')} value={selectedCompanyId} onChange={setSelectedCompanyId}>
                {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
              </Select>
            </Col>
          )}
        </Row>
        <div style={{ marginTop: 16 }}>
          <Button type="primary" icon={<ExportOutlined />} onClick={handleExport} loading={exportLoading}>{t('commerceImport.exportQuery')}</Button>
        </div>
      </Card>

      {exportStatus === 'success' && exportData && (
        <div>
          {exportData.mode === 'flat' && (
            <Card title={`${t('commerceImport.exportResult')}（${exportData.data?.length || 0}）`} extra={<Button icon={<DownloadOutlined />} onClick={() => downloadCsv(exportData.data, 'sales_orders_flat.csv')}>{t('commerceImport.exportDownloadCsv')}</Button>}>
              <Table dataSource={(exportData.data || []).slice(0, 50)} columns={exportData.data?.length > 0 ? Object.keys(exportData.data[0]).map(k => ({ title: k, dataIndex: k, key: k, ellipsis: true, width: 120 })) : []} scroll={{ x: 'max-content', y: 400 }} size="small" pagination={false} rowKey={(_, i) => i} />
            </Card>
          )}
          {exportData.mode === 'split' && (
            <>
              <Card title={`Header（${exportData.headers?.length || 0}）`} extra={<Button icon={<DownloadOutlined />} onClick={() => downloadCsv(exportData.headers, 'sales_orders_headers.csv')}>{t('commerceImport.exportDownloadCsv')}</Button>} style={{ marginBottom: 16 }}>
                <Table dataSource={(exportData.headers || []).slice(0, 50)} columns={exportData.headers?.length > 0 ? Object.keys(exportData.headers[0]).map(k => ({ title: k, dataIndex: k, key: k, ellipsis: true, width: 120 })) : []} scroll={{ x: 'max-content', y: 300 }} size="small" pagination={false} rowKey={(_, i) => i} />
              </Card>
              <Card title={`Detail（${exportData.details?.length || 0}）`} extra={<Button icon={<DownloadOutlined />} onClick={() => downloadCsv(exportData.details, 'sales_orders_details.csv')}>{t('commerceImport.exportDownloadCsv')}</Button>}>
                <Table dataSource={(exportData.details || []).slice(0, 50)} columns={exportData.details?.length > 0 ? Object.keys(exportData.details[0]).map(k => ({ title: k, dataIndex: k, key: k, ellipsis: true, width: 120 })) : []} scroll={{ x: 'max-content', y: 300 }} size="small" pagination={false} rowKey={(_, i) => i} />
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button
              type="default"
              icon={<HistoryOutlined />}
              onClick={() => navigate('/commerce/import-schedule')}
            >
              {t('contactImport.manageSchedule')}
            </Button>
          </Space>
        </Col>
        <Col>
          <Title level={2} style={{ margin: 0, textAlign: 'right' }}>
            {t('commerceImport.title')}
          </Title>
        </Col>
      </Row>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        {
          key: 'import',
          label: <span><ImportOutlined /> {t('commerceImport.tabImport')}</span>,
          children: (
            <div>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle">
                  {showWpa && (
                    <Col>
                      <Radio.Group value={domain} onChange={e => { setDomain(e.target.value); setTargetEntity(e.target.value === 'wpa' ? 'wpaGroupMembers' : 'customers'); resetImport(); }} optionType="button" buttonStyle="solid">
                        <Radio.Button value="commerce">{t('commerceImport.domainCommerce')}</Radio.Button>
                        <Radio.Button value="wpa">{t('commerceImport.domainWpa')}</Radio.Button>
                      </Radio.Group>
                    </Col>
                  )}
                  <Col flex="auto">
                    <Select style={{ width: 300 }} value={targetEntity} onChange={v => { setTargetEntity(v); resetImport(); }}>
                      {allTargetKeys.map(k => <Option key={k} value={k}>{tgt(k)}</Option>)}
                    </Select>
                  </Col>
                  {isTenantAdmin && (
                    <Col>
                      <Select style={{ width: 200 }} allowClear placeholder={t('commerceImport.selectCompany')} value={selectedCompanyId} onChange={setSelectedCompanyId}>
                        {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                      </Select>
                    </Col>
                  )}
                </Row>
              </Card>
              <Spin spinning={loading}>{renderImportWizard()}</Spin>
            </div>
          ),
        },
        {
          key: 'export',
          label: <span><ExportOutlined /> {t('commerceImport.tabExport')}</span>,
          children: renderExportTab(),
        },
      ]} />
    </div>
  );
};

export { generateAutoMapping };

export default CommerceImportPage;
